import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { Field, Input, Select, Textarea } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'
import { AdminTable, Td } from './AdminTable.jsx'

/*
 * Admin — email templates (GET /email-templates). List with search + category
 * filter; edit subject / HTML / text (PUT /email-templates/:id); send a test
 * (POST /email-templates/:id/test). Inventory §3 (email-templates tab).
 */
export default function EmailTemplatesTab() {
  const { t } = useTranslation()
  const { toast, notify } = useFeedback()
  const [all, setAll] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [edit, setEdit] = useState(null)

  const load = () => { setLoading(true); api.get('/email-templates').then(({ data }) => setAll(data?.templates || data || [])).finally(() => setLoading(false)) }
  useEffect(() => { load() }, [])

  const categories = useMemo(() => [...new Set(all.map((tpl) => tpl.category).filter(Boolean))].sort(), [all])
  const s = search.trim().toLowerCase()
  const rows = all.filter((tpl) => (!category || tpl.category === category) && (!s || `${tpl.name} ${tpl.code} ${tpl.subject}`.toLowerCase().includes(s)))

  const columns = [
    { key: 'name', label: t('admin.templates.name') }, { key: 'code', label: t('admin.templates.code') },
    { key: 'category', label: t('admin.templates.category') }, { key: 'lang', label: t('admin.templates.lang') },
    { key: 'active', label: t('admin.companies.status') }, { key: 'actions', label: t('common.actions'), align: 'right' },
  ]

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('admin.templates.searchPlaceholder')} style={{ maxWidth: 320, height: 42 }} />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 'auto', height: 42 }}>
          <option value="">{t('admin.templates.allCategories')}</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
        <span style={{ alignSelf: 'center', fontSize: 13, color: 'var(--color-text-muted)' }}>{t('admin.templates.count', { count: rows.length })}</span>
      </div>
      <AdminTable columns={columns} rows={rows} loading={loading} empty={t('admin.templates.empty')}
        renderRow={(tpl) => (
          <>
            <Td strong>{tpl.name}<div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 400, maxWidth: 380, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tpl.subject}</div></Td>
            <Td><span style={{ fontFamily: 'monospace', fontSize: 12.5 }}>{tpl.code}</span></Td>
            <Td><Badge tone="neutral">{tpl.category}</Badge></Td>
            <Td>{(tpl.language || '').toUpperCase()}</Td>
            <Td><Badge tone={tpl.is_active ? 'success' : 'neutral'}>{tpl.is_active ? t('admin.active') : t('admin.inactive')}</Badge></Td>
            <Td align="right"><IconButton icon="pencil" variant="primary" title={t('common.edit')} onClick={() => setEdit(tpl)} /></Td>
          </>
        )} />
      {edit && <EditModal t={t} tpl={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load() }} toast={toast} notify={notify} />}
    </>
  )
}

function EditModal({ t, tpl, onClose, onSaved, toast, notify }) {
  const [subject, setSubject] = useState(tpl.subject || '')
  const [bodyHtml, setBodyHtml] = useState(tpl.body_html || '')
  const [bodyText, setBodyText] = useState(tpl.body_text || '')
  const [busy, setBusy] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testing, setTesting] = useState(false)

  const save = async () => {
    setBusy(true)
    try { await api.put(`/email-templates/${tpl.id}`, { subject, body_html: bodyHtml, body_text: bodyText }); toast(t('admin.templates.saved'), 'success'); onSaved() }
    catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) } finally { setBusy(false) }
  }
  const sendTest = async () => {
    if (!testEmail) return
    setTesting(true)
    try { await api.post(`/email-templates/${tpl.id}/test`, { to_email: testEmail }); toast(t('admin.templates.testSent'), 'success') }
    catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('admin.templates.testError') }) } finally { setTesting(false) }
  }

  const variables = Array.isArray(tpl.variables) ? tpl.variables : []

  return (
    <Modal open onClose={onClose} maxWidth={720} closeOnBackdrop={false}>
      <div style={{ padding: 26, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18 }}>{tpl.name}</h3>
          <Badge tone="neutral">{tpl.category}</Badge>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', fontFamily: 'monospace', marginBottom: 18 }}>{tpl.code} · {(tpl.language || '').toUpperCase()}</p>

        <Field label={t('admin.templates.subject')}><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
        <Field label={t('admin.templates.bodyHtml')}><Textarea rows={10} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 13 }} /></Field>
        <Field label={t('admin.templates.bodyText')}><Textarea rows={4} value={bodyText} onChange={(e) => setBodyText(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 13 }} /></Field>

        {variables.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>{t('admin.templates.variables')}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{variables.map((v) => <span key={v} style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--color-bg-lilac-strong)', padding: '2px 8px', borderRadius: 6 }}>{`{{ ${v} }}`}</span>)}</div>
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--color-border-row)', paddingTop: 16, marginBottom: 18 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 7 }}>{t('admin.templates.testTo')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@exemple.be" style={{ flex: 1 }} />
            <Button variant="secondary" icon="send" loading={testing} disabled={!testEmail} onClick={sendTest}>{t('admin.templates.test')}</Button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" icon="check" loading={busy} onClick={save}>{t('common.save')}</Button>
        </div>
      </div>
    </Modal>
  )
}
